<script setup lang="ts">
/**
 * A gated tool's payload, before or after its decision (os#911). Before a decision, a text field the gate names
 * editable (`editableFields`, the same list `GatesService.decide` enforces) renders as an input the
 * CEO can change; every other field renders read-only, so a field the API would refuse can never be touched
 * here either. Editing emits the whole edited payload on `update:payload`, ready to send as `decide()`'s edit
 * input. Once the gate is decided and holds an edit (`editedPayload`), shows the original and the edited value
 * side by side instead of a form.
 */
import { computed, ref, watch } from 'vue'
import { textFieldsToEdit } from '@/lib/gatePayloadEdit'

const props = defineProps<{
    /** The gate's own stored payload — the original, always shown, and what an edit is made against. */
    payload: Record<string, unknown>
    /**
     * The field names this gate lets the CEO change, as the API serves them on the gate (`editableFields`): the OS's
     * own list for its tool with the fields the task's gate rows named when it opened (#433).
     */
    editableFields: readonly string[]
    /** Present once the gate is decided and was edited: the whole edited payload, shown beside the original. */
    editedPayload?: Record<string, unknown> | null
}>()
const emit = defineEmits<{
    /** The whole edited payload, sent as `decide()`'s edit input, whenever an editable field changes. */
    'update:payload': [payload: Record<string, unknown>]
}>()

/** The edit in progress: the payload as it stands, so one changed field still emits the whole payload. */
const draft = ref<Record<string, unknown>>({ ...props.payload })
/**
 * A live page re-reads its gates, so the same payload arrives again as a new object: that is no change, and
 * the edit in progress stays on screen. A payload that really differs starts the edit again from it, and says
 * so on `update:payload`, so the parent drops the edit it held and Approve never sends text no longer shown.
 */
watch(
    () => props.payload,
    (payload, previous) => {
        if (JSON.stringify(payload) === JSON.stringify(previous)) {
            return
        }
        draft.value = { ...payload }
        emit('update:payload', { ...payload })
    },
)
/** The payload's own fields, in the order the run wrote them: the form never invents or drops one. */
const fields = computed(() => Object.keys(props.payload))
/** Decided and edited: there is nothing left to change, only the original and the edit to compare. */
const edited = computed(() => props.editedPayload !== null && props.editedPayload !== undefined)

/**
 * The fields that get an input: each one the gate names that the run wrote as text (#433, `textFieldsToEdit`). The
 * card reads the same rule to decide whether to offer an edit at all, so it never promises one this form cannot draw.
 */
const textFields = computed(() => textFieldsToEdit(props.payload, props.editableFields))
function isEditable(field: string): boolean {
    return textFields.value.includes(field)
}

/** A field's value as the CEO reads and edits it: text as written, anything else as its JSON. */
function asText(value: unknown): string {
    if (typeof value === 'string') {
        return value
    }
    if (value === null || value === undefined) {
        return ''
    }
    return JSON.stringify(value)
}

function change(field: string, value: string): void {
    draft.value = { ...draft.value, [field]: value }
    emit('update:payload', { ...draft.value })
}
</script>

<template>
	<div class="gate-payload-editor space-y-3">
		<template v-if="edited">
			<div v-for="field in fields" :key="field" class="grid gap-3 text-sm sm:grid-cols-2">
				<div class="space-y-1">
					<p class="text-muted-foreground text-xs">{{ field }} — as drafted</p>
					<p class="whitespace-pre-wrap">{{ asText(payload[field]) }}</p>
				</div>
				<div class="space-y-1">
					<p class="text-muted-foreground text-xs">{{ field }} — as approved</p>
					<p class="whitespace-pre-wrap">{{ asText(editedPayload?.[field]) }}</p>
				</div>
			</div>
		</template>
		<template v-else>
			<label v-for="field in fields" :key="field" class="block space-y-1 text-sm">
				<span class="text-muted-foreground text-xs">{{ field }}</span>
				<textarea
					v-if="isEditable(field)"
					:name="field"
					:value="asText(draft[field])"
					rows="3"
					class="border-input w-full rounded-md border bg-transparent p-2 text-sm"
					@input="event => change(field, (event.target as HTMLTextAreaElement).value)"
				/>
				<p v-else class="whitespace-pre-wrap">{{ asText(payload[field]) }}</p>
			</label>
		</template>
	</div>
</template>
